import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { entriesApi } from '../../../../api/entries';
import { Button } from '../../atoms/button';
import { Textarea } from '../../atoms/textarea';
import { DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { NoteDialogProps } from './interface';

/**
 * Request Edit / Reject dialog with an optional note. Shared by the Approvals
 * queue and the admin All Entries tab — each caller decides what happens on
 * success (onSuccess) and may override the title/description copy.
 */
export function NoteDialog({
  entry, action, onClose, onSuccess, title, description,
}: NoteDialogProps) {
  const [note, setNote] = useState('');

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  const requestEditMutation = useMutation({
    mutationFn: (n?: string) => entriesApi.requestEdit(entry.id, n),
    onSuccess: handleSuccess,
  });

  const rejectMutation = useMutation({
    mutationFn: (n?: string) => entriesApi.reject(entry.id, n),
    onSuccess: handleSuccess,
  });

  const isSubmitting = requestEditMutation.isPending || rejectMutation.isPending;

  const handleSubmit = () => {
    if (action === 'request-edit') requestEditMutation.mutate(note || undefined);
    else rejectMutation.mutate(note || undefined);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title ?? (action === 'reject' ? 'Reject Entry' : 'Request Edit')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {description ?? (action === 'reject'
            ? 'Optionally explain why this entry is being rejected.'
            : 'Optionally explain what needs to change before resubmission.')}
        </p>
        <Textarea
          placeholder="Add a note (optional)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant={action === 'reject' ? 'destructive' : 'default'}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {action === 'reject' ? 'Reject' : 'Request Edit'}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
